import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../../supabaseClient';
import { REMOTE_BROADCAST_EVENT, remoteTopicForUser, type RemotePresenceState } from '../../types/presentationRemote';
import {
  onlineWarn as warn,
  presenceSignature,
  type OnlineTransport,
  type OnlineTransportCallbacks,
  type OnlineTransportOptions,
} from './onlineTransport';

/**
 * Supabase Realtime 구현 (기존 동작 그대로 이동): private 채널 `cp-remote:${userId}`,
 * broadcast(self:false) + presence(상태 변경 시에만 track, 1.5s 디바운스).
 */
const RETRY_BASE_MS = 2000;
const RETRY_MAX_MS = 30000;
/** 서버 혼잡(동시 연결 한도·속도 제한) 시: 30초부터 최대 5분까지 지수 백오프 */
const LIMITED_RETRY_BASE_MS = 30000;
const LIMITED_RETRY_MAX_MS = 5 * 60 * 1000;
const RATE_LIMIT_RE = /too many|rate.?limit|ConnectionRateLimitReached|ChannelRateLimitReached|limit reached|connection limit|ClientJoinRateLimitReached|capacity/i;
/** Supabase presence track() 디바운스 — 주기 heartbeat 없음, 상태가 바뀔 때만 */
const REMOTE_TRACK_DEBOUNCE_MS = 1500;

export function createSupabaseOnlineTransport(
  opts: OnlineTransportOptions,
  cb: OnlineTransportCallbacks
): OnlineTransport {
  const { userId, sessionId } = opts;
  const topic = remoteTopicForUser(userId);
  let channel: RealtimeChannel | null = null;
  let disposed = false;
  let subscribed = false;
  let enabled = false;
  let retryCount = 0;
  let retryTimer: number | null = null;
  let trackedState: RemotePresenceState | null = null;
  let remoteTrackedSig = '';
  let remoteTrackTimer: number | null = null;
  let hadPresence = false;
  let limited = false;

  const setStatus = cb.onStatus;
  const emitPresence = (list: RemotePresenceState[]) => {
    hadPresence = list.length > 0;
    cb.onPresence(list);
  };

  const trackRemoteNow = () => {
    if (remoteTrackTimer != null) {
      window.clearTimeout(remoteTrackTimer);
      remoteTrackTimer = null;
    }
    const ch = channel;
    if (!ch || !subscribed || !trackedState) return;
    const sig = presenceSignature(trackedState);
    if (sig === remoteTrackedSig) return;
    remoteTrackedSig = sig;
    void ch.track({ ...trackedState, lastSeen: Date.now() }).catch(() => {
      remoteTrackedSig = '';
    });
  };

  const readRemotePresence = (): RemotePresenceState[] => {
    if (!channel) return [];
    try {
      const st = channel.presenceState<RemotePresenceState>();
      const bySession = new Map<string, RemotePresenceState>();
      const seenAt = (p: RemotePresenceState) => p.lastSeen ?? p.since ?? 0;
      Object.values(st).forEach((arr) => {
        (arr ?? []).forEach((p) => {
          if (p && typeof p.sessionId === 'string') {
            const { presence_ref: _ref, ...rest } = p as RemotePresenceState & { presence_ref?: string };
            const cur = bySession.get(p.sessionId);
            if (!cur || seenAt(rest as RemotePresenceState) >= seenAt(cur)) {
              bySession.set(p.sessionId, rest as RemotePresenceState);
            }
          }
        });
      });
      return Array.from(bySession.values());
    } catch {
      return [];
    }
  };

  const scheduleRetry = () => {
    if (disposed || !enabled || retryTimer != null) return;
    setStatus(limited ? 'limited' : 'reconnecting');
    const delay = limited
      ? Math.min(LIMITED_RETRY_MAX_MS, LIMITED_RETRY_BASE_MS * 2 ** Math.min(retryCount, 4))
      : Math.min(RETRY_MAX_MS, RETRY_BASE_MS * 2 ** Math.min(retryCount, 4));
    retryCount += 1;
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      void connect();
    }, delay);
  };

  const teardownChannel = async () => {
    const ch = channel;
    const wasSubscribed = subscribed;
    channel = null;
    subscribed = false;
    remoteTrackedSig = '';
    if (remoteTrackTimer != null) {
      window.clearTimeout(remoteTrackTimer);
      remoteTrackTimer = null;
    }
    if (hadPresence) emitPresence([]);
    if (ch) {
      if (wasSubscribed) {
        try {
          await ch.untrack();
        } catch {
          /* ignore */
        }
      }
      try {
        await supabase.removeChannel(ch);
      } catch {
        /* ignore */
      }
    }
  };

  /** supabase.channel()은 같은 토픽의 기존 채널(제거 진행 중 포함)을 그대로 돌려주므로 먼저 확실히 정리 */
  const removeStaleSameTopic = async () => {
    const full = `realtime:${topic}`;
    const stale = supabase.getChannels().filter((c) => c.topic === full);
    for (const c of stale) {
      try {
        await supabase.removeChannel(c);
      } catch {
        /* ignore */
      }
    }
  };

  const connect = async () => {
    if (disposed || !enabled) return;
    await teardownChannel();
    await removeStaleSameTopic();
    if (disposed || !enabled) return;
    setStatus(limited ? 'limited' : retryCount > 0 ? 'reconnecting' : 'connecting');
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) {
        warn('세션 없음 — 로그인 후 다시 시도하세요.');
        setStatus('error');
        return;
      }
      /* 인자 없이 호출: supabase-js의 accessToken 콜백(현재 세션 JWT)을 사용 */
      await supabase.realtime.setAuth();
      if (disposed || !enabled) return;
      const ch = supabase.channel(topic, {
        config: {
          private: true,
          presence: { key: sessionId },
          broadcast: { self: false, ack: false },
        },
      });
      channel = ch;
      ch.on('broadcast', { event: REMOTE_BROADCAST_EVENT }, (raw: unknown) =>
        cb.onEnvelope((raw as { payload?: unknown })?.payload)
      );
      ch.on('presence', { event: 'sync' }, () => {
        emitPresence(readRemotePresence());
      });
      ch.subscribe((status, err) => {
        if (disposed || channel !== ch) return;
        if (status === 'SUBSCRIBED') {
          subscribed = true;
          retryCount = 0;
          limited = false;
          setStatus('connected');
          /* 참가/재연결 시 1회 track */
          remoteTrackedSig = '';
          trackRemoteNow();
          emitPresence(readRemotePresence());
          return;
        }
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          const msg = err?.message ?? '';
          warn(`채널 ${status}`, msg || '(메시지 없음)', { topic, retry: retryCount });
          if (RATE_LIMIT_RE.test(msg)) {
            /* 혼잡: 로컬 기능은 그대로, 원격만 조용히 일시 중지 후 긴 간격 재시도 */
            if (!limited) retryCount = 0;
            limited = true;
          }
          if (/unauthorized|permission/i.test(msg)) {
            warn(
              'Realtime Authorization 거부 — Supabase에 realtime.messages RLS 정책(마이그레이션 20261020180000_presentation_remote_realtime_auth.sql)이 적용됐는지 확인하세요.'
            );
          }
          subscribed = false;
          void teardownChannel().then(scheduleRetry);
        }
      });
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      if (RATE_LIMIT_RE.test(m)) limited = true;
      warn('연결 시도 실패', m);
      scheduleRetry();
    }
  };

  const { data: authSub } = supabase.auth.onAuthStateChange((event, session) => {
    if (disposed) return;
    if (event === 'SIGNED_OUT' || !session) {
      void teardownChannel();
      setStatus('error');
      return;
    }
    if (!enabled) return;
    if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
      try {
        void supabase.realtime.setAuth();
      } catch {
        /* ignore */
      }
      if (!channel && retryTimer == null) void connect();
    }
  });

  const onOnline = () => {
    /* 혼잡 백오프 중이면 앞당기지 않음 */
    if (disposed || !enabled || subscribed || limited) return;
    if (retryTimer != null) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
    retryCount = 0;
    void connect();
  };
  window.addEventListener('online', onOnline);

  const clearRetry = () => {
    if (retryTimer != null) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
    retryCount = 0;
  };

  const api: OnlineTransport = {
    start: () => {
      if (disposed || enabled) return;
      enabled = true;
      retryCount = 0;
      void connect();
    },
    stop: () => {
      if (!enabled) return;
      enabled = false;
      clearRetry();
      void teardownChannel();
    },
    isReady: () => !!channel && subscribed,
    send: async (env) => {
      const ch = channel;
      if (!ch || !subscribed) return false;
      try {
        const res = await ch.send({ type: 'broadcast', event: REMOTE_BROADCAST_EVENT, payload: env });
        return res === 'ok';
      } catch {
        return false;
      }
    },
    track: (state) => {
      trackedState = state;
      if (!channel || !subscribed) return;
      if (presenceSignature(state) === remoteTrackedSig) return;
      /* 첫 track은 즉시, 이후 상태 변경은 디바운스 */
      if (!remoteTrackedSig) trackRemoteNow();
      else {
        if (remoteTrackTimer != null) window.clearTimeout(remoteTrackTimer);
        remoteTrackTimer = window.setTimeout(trackRemoteNow, REMOTE_TRACK_DEBOUNCE_MS);
      }
    },
    reconnect: () => {
      /* 기존 재시도 로직을 처음부터 다시 */
      if (disposed) return;
      api.stop();
      api.start();
    },
    dispose: () => {
      if (disposed) return;
      disposed = true;
      enabled = false;
      if (retryTimer != null) window.clearTimeout(retryTimer);
      window.removeEventListener('online', onOnline);
      try {
        authSub.subscription.unsubscribe();
      } catch {
        /* ignore */
      }
      void teardownChannel(); /* untrack → removeChannel 순서 */
    },
  };
  return api;
}
